import React, { useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSeedling,
  faHandHoldingUsd,
  faUser,
  faCheckCircle,
  faFileContract,
} from "@fortawesome/free-solid-svg-icons";

const AgriLinkSupportFarmer = () => {
  const [donationAmount, setDonationAmount] = useState("");
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [agree, setAgree] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // Dummy farmer data
  const farmers = [
    {
      id: 1,
      name: "Ramesh Kumar",
      location: "Maharashtra, India",
      need: "Seeds & Fertilizers",
      goal: 10000,
      raised: 4500,
    },
    {
      id: 2,
      name: "Amina Begum",
      location: "Bangladesh",
      need: "Irrigation Pump",
      goal: 20000,
      raised: 9500,
    },
    {
      id: 3,
      name: "John Mwangi",
      location: "Kenya",
      need: "Organic Fertilizer",
      goal: 15000,
      raised: 8000,
    },
  ];

  const handleDonate = (farmer) => {
    if (!donationAmount || !agree) {
      setSuccessMsg("⚠️ Please enter an amount and agree to Terms & Conditions.");
      return;
    }
    setSelectedFarmer(farmer);
    setSuccessMsg(
      `✅ Thank you! Your support of ₹${donationAmount} has been pledged to ${farmer.name}.`
    );
    setDonationAmount("");
    setAgree(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-black text-white min-h-screen">
      <h1 className="text-2xl font-bold bg-green-700 px-4 py-2 rounded-lg text-yellow-200 flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faSeedling} /> Think5Whys Support a Farmer
      </h1>
      <p className="text-gray-100 mb-8">
        Join our mission to empower farmers worldwide. Your contribution helps
        them access seeds, fertilizers, irrigation, and resources to sustain
        their livelihood. Every small step makes a big impact.
      </p>

      {/* Farmer Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {farmers.map((farmer) => {
          const percentage = Math.min(
            100,
            Math.round((farmer.raised / farmer.goal) * 100)
          );
          return (
            <div
              key={farmer.id}
              className="bg-white p-4 rounded-xl shadow hover:shadow-lg transition"
            >
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} /> {farmer.name}
              </h2>
              <p className="text-sm text-gray-500">{farmer.location}</p>
              <p className="mt-2 text-gray-700">
                <strong>Need:</strong> {farmer.need}
              </p>
              <p className="text-gray-600 text-sm">
                Goal: ₹{farmer.goal.toLocaleString()} | Raised: ₹
                {farmer.raised.toLocaleString()}
              </p>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                <div
                  className="bg-green-600 h-3 rounded-full"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{percentage}% funded</p>

              {/* Donation Input */}
              <div className="mt-4">
                <input
                  type="number"
                  placeholder="Enter amount (₹)"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  className="border-2 rounded-lg px-3 py-2 w-full text-sm mb-2 text-black"
                />
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={agree}
                    onChange={() => setAgree(!agree)}
                  />
                  I agree to the{" "}
                  <span className="text-blue-600 underline cursor-pointer">
                    Terms & Conditions
                  </span>
                </label>
                <button
                  onClick={() => handleDonate(farmer)}
                  className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faHandHoldingUsd} /> Contribute
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Success message */}
      {successMsg && (
        <div className="mt-6 p-4 bg-green-100 border border-green-300 rounded-lg text-green-800 flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} /> {successMsg}
        </div>
      )}

      {/* Terms & Conditions Section */}
      <div className="mt-4 mb-4 bg-white shadow p-6 rounded-xl">
        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <FontAwesomeIcon icon={faFileContract} /> Terms & Conditions
        </h3>
        <ul className="list-disc pl-5 text-sm text-gray-600 space-y-2">
          <li>
            Donations are voluntary and non-refundable. We only act as a
            platform connecting supporters with farmers.
          </li>
          <li>
            Funds will be allocated to the farmer’s stated needs (e.g., seeds,
            irrigation, fertilizers).
          </li>
          <li>
            We aim for transparency but cannot guarantee outcomes due to
            agricultural risks (weather, crop failure, etc.).
          </li>
          <li>
            Supporters may receive periodic updates/photos on fund utilization.
          </li>
          <li>
            This platform is not a money-lending service. It is purely for
            social good.
          </li>
        </ul>
      </div>
      {/* Footer */}
    <footer className="bg-green-700 text-white py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
        <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
        <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <Link to="/about-us" className="text-white hover:text-green-400 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-green-400 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-green-400 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-green-400 transition">Contact</Link>
        </div>
        </div>
    </footer>
    </div>
  );
};

export default AgriLinkSupportFarmer;
